import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { Printer, Star } from 'lucide-react';
import { BRAND } from '@shripos/shared/brand';
import { Alert, Button, Card, CardBody, PageLoader, Textarea, cn } from '@shripos/ui';
import { ReceiptView } from '../../components/ReceiptView.jsx';
import { API_ORIGIN } from '../../lib/api.js';
import { printNode } from '../../lib/print.js';

/** "Rate your visit" (Settings → Customers & reports → Feedback). One rating per bill. */
function FeedbackBox({ token, feedback }) {
  const qc = useQueryClient();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [state, setState] = useState({ busy: false, error: null });
  if (!feedback?.enabled) return null;
  if (feedback.given)
    return <p className="text-sm text-slate-600">Thank you for your feedback ({'★'.repeat(feedback.given.rating)}).</p>;
  const send = async () => {
    setState({ busy: true, error: null });
    try {
      const res = await fetch(`${API_ORIGIN}/api/receipts/${encodeURIComponent(token)}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, ...(comment.trim() ? { comment: comment.trim() } : {}) }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok && res.status !== 409) throw new Error(body?.error?.message ?? 'Could not send. Please try again.');
      await qc.invalidateQueries({ queryKey: ['public-receipt', token] });
    } catch (err) {
      setState({ busy: false, error: err.message });
    }
  };
  return (
    <Card className="w-full">
      <CardBody className="space-y-3">
        <p className="text-center font-medium text-slate-800">How was your visit?</p>
        <div className="flex justify-center gap-1" role="radiogroup" aria-label="Rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={rating === n}
              aria-label={`${n} star${n > 1 ? 's' : ''}`}
              onClick={() => setRating(n)}
              className="p-1"
            >
              <Star className={cn('size-8', n <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300')} />
            </button>
          ))}
        </div>
        {rating > 0 && (
          <>
            <Textarea
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 500))}
              placeholder={rating <= 3 ? 'What could we do better?' : 'Anything you loved? (optional)'}
              aria-label="Comment"
            />
            {state.error && <Alert tone="danger">{state.error}</Alert>}
            <Button className="w-full" loading={state.busy} onClick={send}>
              Send feedback
            </Button>
          </>
        )}
      </CardBody>
    </Card>
  );
}

/** Customer-facing e-receipt behind the signed link shared on WhatsApp / email. No login. */
export function PublicReceiptPage() {
  const { token } = useParams();
  const receipt = useQuery({
    queryKey: ['public-receipt', token],
    queryFn: async () => {
      const res = await fetch(`${API_ORIGIN}/api/receipts/${encodeURIComponent(token)}`);
      if (!res.ok)
        throw new Error(res.status === 404 ? 'This receipt link is not valid.' : 'Could not load the receipt.');
      return res.json();
    },
    retry: false,
  });
  return (
    <div className="min-h-full bg-stone-100 px-3 py-6">
      {receipt.isLoading ? (
        <PageLoader label="Loading your receipt…" />
      ) : receipt.error ? (
        <div className="mx-auto max-w-sm">
          <Alert tone="danger">{receipt.error.message}</Alert>
        </div>
      ) : (
        <div className="mx-auto flex max-w-md flex-col items-center gap-4">
          <ReceiptView model={receipt.data} className="rounded-lg shadow-md" />
          <FeedbackBox token={token} feedback={receipt.data.feedback} />
          <Button
            variant="secondary"
            icon={Printer}
            onClick={() => printNode(<ReceiptView model={receipt.data} />, { width: receipt.data.paperWidth })}
          >
            Print / save as PDF
          </Button>
          <p className="text-xs text-slate-500">Digital receipt by {BRAND.productName}</p>
        </div>
      )}
    </div>
  );
}
